import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useUIStore } from '../store/uiStore';

interface UseSheetPresentationOptions {
  /**
   * uiStore.sheetOpenTick — pass for uiStore-driven sheets so present()
   * re-fires when the same sheet is re-opened (see the uiStore comment).
   * Omit for visible-prop sheets.
   */
  tick?: number;
  /**
   * Per-sheet dismiss work (clear local state, conditional closeSheet(),
   * markTourSeen(), onClose()…). Runs AFTER the hook releases its tab-bar
   * hold. May change identity freely — stashed in a ref internally so the
   * returned handleDismiss stays stable.
   */
  onDismiss?: () => void;
}

/**
 * The shared BottomSheetModal lifecycle: present() + ref-counted
 * hideTabBar when isOpen flips true, dismiss() when false, and a stable
 * handleDismiss that releases the tab-bar hold (CLAUDE.md hard rule 4) —
 * pass it as the modal's `onDismiss`. The hold is released only via
 * onDismiss (after the dismiss animation), never in the close effect.
 *
 * Open-time work (form resets, re-seeding) stays in a per-sheet
 * `useEffect(..., [isOpen, ...])` — this hook owns only the lifecycle.
 * For inline (non-modal) BottomSheets use useBottomSheetTabBarVisibility.
 */
export function useSheetPresentation(
  ref: RefObject<BottomSheetModal | null>,
  isOpen: boolean,
  options?: UseSheetPresentationOptions
): { handleDismiss: () => void } {
  const hideTabBar = useUIStore((s) => s.hideTabBar);
  const showTabBar = useUIStore((s) => s.showTabBar);

  const onDismissRef = useRef(options?.onDismiss);
  onDismissRef.current = options?.onDismiss;

  const tick = options?.tick;
  useEffect(() => {
    if (isOpen) {
      ref.current?.present();
      hideTabBar();
    } else {
      ref.current?.dismiss();
    }
  }, [isOpen, tick, hideTabBar, ref]);

  const handleDismiss = useCallback(() => {
    showTabBar();
    onDismissRef.current?.();
  }, [showTabBar]);

  return { handleDismiss };
}
